import {
  ArrowLeftIcon,
  Badge,
  ExternalIcon,
  formatMoney,
  formatDate,
  HelpIcon,
  IconButton,
  Skeleton,
} from '@setel/portal-ui';
import * as React from 'react';
import { AxiosError } from 'axios';
import { Link, useLocation } from 'react-router-dom';
import SetelLogo from 'assets/icons/icon-72x72.svg';
import { TRANSACTION_MIX_PAYMENT_METHODS } from 'shared/consts/payment.const';
import { PaymentTransaction } from 'shared/interfaces/payments.interface';
import { getStoreOrderBadgeColor } from '../store-order.helper';
import { useStoreOrder } from '../store-order.queries';
import {
  usePaymentTransactionDetails,
  usePaymentTransactions,
} from 'modules/fuel-orders/fuel-orders.queries';
import { getPaymentMethod } from 'modules/fuel-orders/fuel-orders.helper';
import { environments } from 'environments/environment';
import { LoadingPage } from 'modules/pages/loading-page';
import {
  NavigationDropdownSearchQueryKey,
  NavigationDropdownSearchQueryValue,
} from 'components/navigation-dropdown';
import { pageview } from 'shared/analytics/gtag';

type StoreOrderDetailsProps = {
  orderId: string;
  isLoading?: boolean;
};

export function StoreOrderDetails({ orderId }: StoreOrderDetailsProps) {
  // added ga for url tracking
  let location = useLocation();
  React.useEffect(() => {
    pageview(location.pathname, 'Store Order');
  }, [location]);

  const {
    data: storeOrder,
    isError: storeOrderError,
    isLoading: storeOrderLoading,
  } = useStoreOrder(orderId, {
    retry: (retryCount, err) => {
      const error = err as AxiosError;
      return retryCount < 3 && error?.response?.status !== 404;
    },
  });

  const { data: orderTrx, isLoading: orderTrxLoading } = usePaymentTransactions(
    { orderId },
    { enabled: Boolean(storeOrder) }
  );

  const captureTrx =
    orderTrx?.items && orderTrx.items.find((trx) => trx.type === 'PURCHASE');

  const { data: trxDetails, isLoading: trxDetailsLoading } =
    usePaymentTransactionDetails(captureTrx?.id as string, {
      enabled: Boolean(captureTrx),
    });

  return (
    <>
      {!storeOrder && storeOrderLoading ? (
        <LoadingPage />
      ) : storeOrderError ? (
        <StoreOrderNotFoundPage />
      ) : (
        <div className="border-gray-200 border">
          <div className="px-4 py-5 flex items-center space-x-4">
            <Link
              className="flex items-center"
              to={`/accounts/${storeOrder?.userId}?${NavigationDropdownSearchQueryKey}=${NavigationDropdownSearchQueryValue.STORE_ORDER}`}
            >
              <IconButton className="p-0">
                <ArrowLeftIcon className="w-4 h-4 text-lightgrey" />
              </IconButton>
            </Link>
            <h1 className="text-lg font-bold">Store order details</h1>
          </div>
          <hr />
          <div className="px-4 py-5">
            <dl className="grid grid-cols-1 gap-5">
              <DescItem
                label="Status"
                value={
                  <Badge
                    color={getStoreOrderBadgeColor(
                      storeOrder?.storeOrderStatus
                    )}
                    className="uppercase"
                  >
                    {storeOrder?.storeOrderStatus
                      ? storeOrder.storeOrderStatus
                      : '-'}
                  </Badge>
                }
              />
              <DescItem
                label="Amount"
                value={
                  storeOrder?.totalAmount
                    ? `RM${formatMoney(storeOrder.totalAmount)}`
                    : '-'
                }
              />
              <DescItem
                label="Order ID"
                value={storeOrder?.orderId ? storeOrder.orderId : '-'}
                link={
                  storeOrder?.orderId
                    ? `${environments.webAdminUrl}/store-orders/over-counter/${storeOrder?.orderId}`
                    : ''
                }
              />
              <div className="tracking-wide">
                <DescItem
                  label="Petrol Station"
                  value={
                    storeOrder?.stationName
                      ? `PETRONAS ${storeOrder.stationName
                          .replace('PETRONAS', '')
                          .toLocaleLowerCase()}`
                      : '-'
                  }
                />
              </div>
              <DescItem
                label="Mesra points earned"
                value={
                  storeOrder?.loyalty?.petronas?.earnedPoints
                    ? `${storeOrder.loyalty.petronas.earnedPoints} pts`
                    : '-'
                }
              />
              <DescItem
                label="Mesra balance"
                value={
                  storeOrder?.loyalty?.petronas?.balance
                    ? `${storeOrder.loyalty.petronas.balance} pts`
                    : '-'
                }
              />
              <DescItem
                label="Payment Method"
                value={
                  captureTrx && trxDetails ? (
                    <PaymentMethod
                      paymentTrx={captureTrx}
                      walletBalance={
                        trxDetails?.walletBalance
                          ? formatMoney(trxDetails.walletBalance)
                          : '-'
                      }
                    />
                  ) : (
                    '-'
                  )
                }
                isLoading={orderTrxLoading || trxDetailsLoading}
              />
              <DescItem
                label="Created On"
                value={
                  storeOrder?.createdAt ? formatDate(storeOrder.createdAt) : '-'
                }
              />
            </dl>
          </div>
        </div>
      )}
    </>
  );
}

type DescItemProps = {
  label: string | React.ReactNode;
  value: string | React.ReactNode;
  isLoading?: boolean;
  link?: string;
};

export function DescItem({
  label,
  value,
  isLoading = false,
  link,
}: DescItemProps) {
  return (
    <div>
      <div className="block tracking-wide leading-3 text-lightgrey text-xs font-bold uppercase">
        {label}
      </div>
      {isLoading ? (
        <Skeleton width="medium" className="animate-pulse" />
      ) : (
        <>
          {typeof value === 'string' ? (
            <dd className="text-base capitalize">
              {value}
              {link && (
                <a
                  href={link}
                  className="text-brand-500 tracking-1 font-semibold"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalIcon
                    className="ml-1 inline"
                    style={{ width: 13, height: 13 }}
                  />
                </a>
              )}
            </dd>
          ) : (
            <dd>{value}</dd>
          )}
        </>
      )}
    </div>
  );
}

type PaymentMethodProps = {
  paymentTrx: PaymentTransaction;
  walletBalance: string;
};

function PaymentMethod({ paymentTrx, walletBalance }: PaymentMethodProps) {
  const paymentMethod = getPaymentMethod(paymentTrx);

  return (
    <div className="h-9 flex items-center">
      {paymentMethod === TRANSACTION_MIX_PAYMENT_METHODS.walletSetel.text ? (
        <>
          <img
            src={SetelLogo}
            alt={paymentTrx.type}
            width="47px"
            height="31px"
          />
          <div className="flex flex-row place-content-center text-sm ml-2">
            <p className="font-normal">Setel Wallet</p>
            <p className="mx-2">•</p>
            <p className="font-normal">RM{walletBalance}</p>
          </div>
        </>
      ) : (
        <div>
          <p className="text-sm">{paymentMethod}</p>
          <p className="text-xs">Balance: RM{walletBalance}</p>
        </div>
      )}
    </div>
  );
}

export function StoreOrderNotFoundPage() {
  return (
    <div className="flex flex-col items-center justify-center h-screen">
      <HelpIcon className="text-gray-500 h-9 w-9 mb-3.5" />
      <div className="text-center leading-5 mb-5">
        <p data-testid="error" className="text-gray-500">
          Unable to find store order.
        </p>
        <p className="text-gray-500">Information is incomplete.</p>
      </div>
      <a
        target="_blank"
        href={`${environments.webAdminUrl}`}
        className="text-brand-500 tracking-1 font-semibold"
        rel="noopener noreferrer"
      >
        <span className="text-xs">OPEN PORTAL</span>
        <ExternalIcon className="inline ml-2" />
      </a>
    </div>
  );
}
